import React, { useState, useEffect, lazy, Suspense } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import ErrorBoundary from './ErrorBoundary';
import NewLandingPage from './LandingPage';
import { PRICING_TIERS, SERVICE_PROVIDER_PRICING, CUSTOM_PLAN, canAddProperty, getUpgradeSuggestion, TRIAL_DAYS, TENANT_FEE, fmtPrice } from './pricingConfig';
import { fetchPlans, FALLBACK_PLANS, dollars } from './planService';
import './landing.css';

// ── Chunk-load recovery ─────────────────────────────────────────────────
// Every deploy renames the hashed chunks. A device that loaded index.html
// BEFORE a deploy will request chunk files that no longer exist the moment
// it lazy-loads a route — the import rejects and, unguarded, the screen
// goes blank. The fix: one automatic reload (which fetches the fresh
// index.html and therefore fresh chunk names), loop-guarded via
// sessionStorage so a genuinely broken build degrades to the root
// ErrorBoundary instead of a reload loop.
const CHUNK_RELOAD_FLAG = 'propmn-chunk-reload';
const flagStore = {
  get: () => { try { return sessionStorage.getItem(CHUNK_RELOAD_FLAG); } catch { return null; } },
  set: () => { try { sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1'); } catch { /* privacy mode */ } },
  clear: () => { try { sessionStorage.removeItem(CHUNK_RELOAD_FLAG); } catch { /* ignore */ } },
};
const lazyRetry = (loader) => lazy(() =>
  loader()
    .then((m) => { flagStore.clear(); return m; })
    .catch((err) => {
      if (!flagStore.get()) {
        flagStore.set();
        window.location.reload();
        return new Promise(() => {}); // hold Suspense while the page reloads
      }
      throw err; // second failure in a row = real problem → ErrorBoundary
    })
);

// ── Route-level code splitting ──────────────────────────────────────────
// Only the landing page ships in the entry bundle: a public visitor gets the
// smallest possible first load, and each dashboard downloads only for the
// role that actually uses it. Loaders are kept as named references so the
// SAME import() serves both React.lazy and prefetching — import() caches,
// so warming a chunk early is free and never double-downloads.
const loadLandlordDashboard = () => import('./LandlordDashboard');
const loadRenterDashboard = () => import('./RenterDashboard');
const loadServiceProviderDashboard = () => import('./ServiceProviderDashboard');
const loadAdminDashboard = () => import('./AdminDashboard');
const loadAgencyDashboard = () => import('./AgencyDashboard');

const LandlordDashboard = lazyRetry(loadLandlordDashboard);
const RenterDashboard = lazyRetry(loadRenterDashboard);
const ServiceProviderDashboard = lazyRetry(loadServiceProviderDashboard);
const AdminDashboard = lazyRetry(loadAdminDashboard);
const AgencyDashboard = lazyRetry(loadAgencyDashboard);

const StaffInviteSignup = lazyRetry(() => import('./StaffInviteSignup'));
const AgencySignup = lazyRetry(() => import('./AgencySignup'));
const ForgotPasswordPage = lazyRetry(() => import('./AuthPages').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazyRetry(() => import('./AuthPages').then(m => ({ default: m.ResetPasswordPage })));
const EmailVerificationPage = lazyRetry(() => import('./AuthPages').then(m => ({ default: m.EmailVerificationPage })));

// Public doc pages (v4.0) — rarely-visited, no reason to weigh down the entry
const TermsOfService = lazyRetry(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazyRetry(() => import('./pages/PrivacyPolicy'));
const ChangelogPage = lazyRetry(() => import('./pages/ChangelogPage'));
const HousingVouchersPage = lazyRetry(() => import('./pages/HousingVouchersPage'));
const DeleteAccountPage = lazyRetry(() => import('./pages/DeleteAccountPage'));
const ExtendedStayPage = lazyRetry(() => import('./pages/ExtendedStayPage'));
const ApplyPage = lazyRetry(() => import('./pages/ApplyPage'));
const OwnerInviteSignup = lazyRetry(() => import('./pages/OwnerInviteSignup'));
const TeamInviteSignup = lazyRetry(() => import('./pages/TeamInviteSignup'));
const ListingPage = lazyRetry(() => import('./pages/ListingPage'));
const EnterprisePage = lazyRetry(() => import('./pages/EnterprisePage'));
const HowItWorksPage = lazyRetry(() => import('./pages/HowItWorksPage'));
const ForAgenciesPage = lazyRetry(() => import('./pages/ForAgenciesPage'));
const VerifyPage = lazyRetry(() => import('./pages/VerifyPage'));
const RentHistoryPage = lazyRetry(() => import('./pages/RentHistoryPage'));
const SignPage = lazyRetry(() => import('./pages/SignPage'));
const PartnerPage = lazyRetry(() => import('./pages/PartnerPage'));
const GovPortalPage = lazyRetry(() => import('./pages/GovPortalPage'));
const BaltimorePage = lazyRetry(() => import('./pages/BaltimorePage'));
const PhiladelphiaPage = lazyRetry(() => import('./pages/PhiladelphiaPage'));
const WilmingtonPage = lazyRetry(() => import('./pages/WilmingtonPage'));
const SecurityPage = lazyRetry(() => import('./pages/SecurityPage'));
const GuidesPage = lazyRetry(() => import('./pages/GuidesPage'));
const CommercialPage = lazyRetry(() => import('./pages/CommercialPage'));
const OwnerDashboard = lazyRetry(() => import('./OwnerDashboard'));

// role → chunk loader. Used to warm the right dashboard the moment we know
// who's logging in (auth modal open / stored session restore), so by the
// time credentials verify the code is already in cache — no loading flash.
const DASHBOARD_LOADERS = {
  landlord: loadLandlordDashboard,
  renter: loadRenterDashboard,
  service_provider: loadServiceProviderDashboard,
  agency: loadAgencyDashboard,
  admin: loadAdminDashboard,
};
const prefetchDashboard = (role) => {
  const load = DASHBOARD_LOADERS[role];
  if (load) load().catch(() => { /* prefetch is best-effort; lazyRetry() retries on render */ });
};

// Suspense fallback — same spinner as the session-restore state, so a chunk
// load looks identical to any other brief loading moment.
const RouteLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
  </div>
);

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// ============================================
// LANDING PAGE - Landlord Focused
// ============================================
const LandingPage = ({ onLogin }) => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authType, setAuthType] = useState('landlord');
  const [authMode, setAuthMode] = useState('login');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');

  const openAuth = (type, mode = 'login') => {
    setAuthType(type);
    setAuthMode(mode);
    setShowAuthModal(true);
    // Warm the dashboard chunk for this role while the user types their
    // credentials — by submit time it's cached and login feels instant.
    prefetchDashboard(type);
  };

  // Deep link from conversion pages (/baltimore, /commercial): /?signup=landlord
  // opens the register modal directly — a plain <a> can't reach openAuth.
  useEffect(() => {
    const want = new URLSearchParams(window.location.search).get('signup');
    if (want === 'landlord' || want === 'service_provider') openAuth(want, 'register');
  }, []);

  const navigateTo = (page) => {
    setCurrentPage(page);
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  // Render different pages
  if (currentPage === 'pricing') {
    return (
      <>
        <PricingPage onBack={() => navigateTo('home')} onAuth={openAuth} />
        {showAuthModal && (
          <AuthModal type={authType} mode={authMode}
            onClose={() => setShowAuthModal(false)} onLogin={onLogin}
            onSwitchMode={(mode) => setAuthMode(mode)} onSwitchType={(type) => setAuthType(type)} />
        )}
      </>
    );
  }
  if (currentPage === 'service-providers') {
    return (
      <>
        <ServiceProvidersPage onBack={() => navigateTo('home')} onAuth={openAuth} />
        {showAuthModal && (
          <AuthModal type={authType} mode={authMode}
            onClose={() => setShowAuthModal(false)} onLogin={onLogin}
            onSwitchMode={(mode) => setAuthMode(mode)} onSwitchType={(type) => setAuthType(type)} />
        )}
      </>
    );
  }

  return (
    <>
      <NewLandingPage onAuth={openAuth} navigateTo={navigateTo} />
      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal 
          type={authType} 
          mode={authMode} 
          onClose={() => setShowAuthModal(false)} 
          onLogin={onLogin}
          onSwitchMode={(mode) => setAuthMode(mode)}
          onSwitchType={(type) => setAuthType(type)}
        />
      )}
    </>
  );
};

// ============================================
// PRICING PAGE
// ============================================
// ── Shared shell for public pages: the landing's pill nav + footer feel ──
const PublicShell = ({ onBack, onAuth, children }) => (
  <div className="lp" style={{ background: 'var(--paper)', minHeight: '100vh' }}>
    <nav className="lnav"><div className="nav-pill lite" style={{ opacity: 1, transform: 'none' }}>
      <button className="logo" onClick={onBack}><img src="/propmn-logo.png" alt="PropMN" /></button>
      <div className="nav-links shell-links">
        <button onClick={onBack}>← Home</button>
        <button onClick={() => onAuth('landlord', 'login')}>Log in</button>
      </div>
      <button className="btn-nav" onClick={() => onAuth('landlord', 'register')}>Start free</button>
    </div></nav>
    {children}
    <footer style={{ padding: '30px 0 50px', textAlign: 'center' }}>
      <span style={{ fontSize: 13, color: '#8b8496' }}>© {new Date().getFullYear()} PropMN — tenants pay ${TENANT_FEE}, forever.</span>
    </footer>
    <style>{`
      .lp .pp-wrap{max-width:1120px;margin:0 auto;padding:120px 24px 40px}
      .lp .pp-head{text-align:center;margin-bottom:48px}
      .lp .pp-head h2{font-family:'Bricolage Grotesque',sans-serif;font-size:clamp(34px,5vw,52px);line-height:1.05;color:var(--ink);letter-spacing:-.02em}
      .lp .pp-head p{color:#6b6477;font-size:17px;margin-top:14px;max-width:560px;margin-left:auto;margin-right:auto}
      .lp .pp-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:18px;align-items:stretch}
      .lp .pp-card{background:#fff;border:1px solid rgba(23,18,31,.09);border-radius:20px;padding:26px 24px;display:flex;flex-direction:column;box-shadow:0 2px 14px rgba(23,18,31,.05)}
      .lp .pp-card.hot{border:2px solid var(--brand);box-shadow:0 12px 40px rgba(124,92,255,.18);position:relative}
      .lp .pp-card.hot .pp-flag{position:absolute;top:-12px;left:50%;transform:translateX(-50%);background:var(--brand);color:#fff;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;padding:4px 14px;border-radius:99px}
      .lp .pp-name{font-weight:700;font-size:16px;color:var(--ink)}
      .lp .pp-price{font-family:'Spline Sans Mono',monospace;font-size:34px;font-weight:600;color:var(--ink);margin:10px 0 2px}
      .lp .pp-price small{font-size:13px;color:#8b8496;font-family:'Instrument Sans',sans-serif;font-weight:400}
      .lp .pp-desc{font-size:13px;color:#8b8496;margin-bottom:16px}
      .lp .pp-feats{list-style:none;padding:0;margin:0 0 22px;flex:1}
      .lp .pp-feats li{font-size:13.5px;color:#4e4760;padding:5px 0 5px 22px;position:relative}
      .lp .pp-feats li:before{content:'✓';position:absolute;left:0;color:var(--brand);font-weight:700}
      .lp .pp-cta{background:linear-gradient(135deg,var(--brand),#9D6BFF);color:#fff;border-radius:12px;padding:12px;font-weight:600;font-size:14px;text-align:center;transition:transform .15s;cursor:pointer}
      .lp .pp-cta:hover{transform:translateY(-2px)}
      .lp .pp-cta.ghost{background:#fff;color:var(--ink);border:1px solid rgba(23,18,31,.15)}
      .lp .pp-note{background:var(--brand-soft);border:1px solid rgba(124,92,255,.25);border-radius:16px;padding:18px 22px;text-align:center;font-size:14.5px;color:var(--ink);margin:34px 0}
    `}</style>
  </div>
);

// ── Public document/marketplace pages: shell nav + self-contained auth modal ──
function PublicDocPage({ Body, onLogin }) {
  const [show, setShow] = useState(false);
  const [type, setType] = useState('landlord');
  const [mode, setMode] = useState('login');
  const open = (t, m = 'login') => { setType(t); setMode(m); setShow(true); };
  return (
    <>
      <PublicShell onBack={() => { window.location.href = '/'; }} onAuth={open}>
        <div style={{ paddingTop: 84 }}><Body /></div>
      </PublicShell>
      {show && (
        <AuthModal type={type} mode={mode}
          onClose={() => setShow(false)}
          onLogin={(u) => { onLogin(u); window.location.href = '/'; }}
          onSwitchMode={setMode} onSwitchType={setType} />
      )}
    </>
  );
}

// ── PRICING — static from pricingConfig: renders instantly, no fetch, no spinner ──
const PricingPage = ({ onBack, onAuth }) => {
  const [livePlansP, setLivePlansP] = React.useState(FALLBACK_PLANS);
  React.useEffect(() => { fetchPlans().then(setLivePlansP).catch(() => {}); }, []);
  const tiers = Object.values({ starter: livePlansP.landlord_starter, professional: livePlansP.landlord_professional, business: livePlansP.landlord_business, enterprise: livePlansP.landlord_enterprise });
  return (
    <PublicShell onBack={onBack} onAuth={onAuth}>
      <div className="pp-wrap">
        <div className="pp-head">
          <span className="eyebrow" style={{ marginBottom: 18 }}>PRICING</span>
          <h2>Priced for real portfolios,<br />not REITs</h2>
          <p>Every plan starts with a {TRIAL_DAYS}-day free trial — cancel anytime before it ends and you pay nothing. Tenants pay ${TENANT_FEE} — forever.</p>
        </div>
        <div className="pp-grid">
          {tiers.map(t => (
            <div key={t.id} className={`pp-card${t.popular ? ' hot' : ''}`}>
              {t.popular && <span className="pp-flag">Most popular</span>}
              <span className="pp-name">{t.name}</span>
              <div className="pp-price">{dollars(t.price)}<small> /month</small></div>
              <span className="pp-desc">{t.description}</span>
              <ul className="pp-feats">{t.features.map((f, i) => <li key={i}>{f}</li>)}</ul>
              <button className="pp-cta" onClick={() => onAuth('landlord', 'register')}>Start {TRIAL_DAYS}-day free trial</button>
            </div>
          ))}
        </div>
        <div className="pp-note">
          <b>Tenant screening built in:</b> background-check any applicant from $39.99 per check — applicant-consent flow, criminal &amp; eviction options, clear/consider results. Pay per check, on any plan.
        </div>
        <div className="pp-grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))' }}>
          <div className="pp-card">
            <span className="pp-name">{SERVICE_PROVIDER_PRICING.name}</span>
            <div className="pp-price">{fmtPrice(SERVICE_PROVIDER_PRICING.price)}<small> /month</small></div>
            <span className="pp-desc">{SERVICE_PROVIDER_PRICING.description}</span>
            <ul className="pp-feats">{SERVICE_PROVIDER_PRICING.features.map((f, i) => <li key={i}>{f}</li>)}</ul>
            <button className="pp-cta ghost" onClick={() => onAuth('service_provider', 'register')}>Join as a service pro</button>
          </div>
          <div className="pp-card">
            <span className="pp-name">{CUSTOM_PLAN?.name || 'Enterprise'}</span>
            <div className="pp-price" style={{ fontSize: 26 }}>Custom pricing</div>
            <span className="pp-desc">{CUSTOM_PLAN?.description || 'Custom limits, onboarding & support for large portfolios, municipalities and housing agencies.'}</span>
            <ul className="pp-feats">{(CUSTOM_PLAN?.features || ['Unlimited properties & tenants', 'Organization seats & access levels', 'Housing-agency portals', 'Priority support']).map((f, i) => <li key={i}>{f}</li>)}</ul>
            <a className="pp-cta ghost" href="/enterprise" style={{ display: 'block', textDecoration: 'none' }}>Request setup</a>
          </div>
        </div>
      </div>
    </PublicShell>
  );
};

// ── SERVICE PROS — the pitch to contractors, in the landing language ──
const ServiceProvidersPage = ({ onBack, onAuth }) => (
  <PublicShell onBack={onBack} onAuth={onAuth}>
    <div className="pp-wrap" style={{ maxWidth: 920 }}>
      <div className="pp-head">
        <span className="eyebrow" style={{ marginBottom: 18 }}>SERVICE PROS</span>
        <h2>The jobs come to you</h2>
        <p>Plumbers, electricians, cleaners, handypeople — landlords on PropMN dispatch real maintenance work to local pros every day. Your profile puts you in that rotation.</p>
      </div>
      <div className="pp-grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', marginBottom: 34 }}>
        <div className="pp-card"><span className="pp-name">🔧 Real jobs, not leads-for-sale</span><p style={{ fontSize: 13.5, color: '#6b6477', marginTop: 8 }}>Landlords assign you actual maintenance requests with photos, addresses and urgency — no bidding wars, no pay-per-lead.</p></div>
        <div className="pp-card"><span className="pp-name">🧾 Invoice in the same place</span><p style={{ fontSize: 13.5, color: '#6b6477', marginTop: 8 }}>Send invoices to landlords inside PropMN, track payment status, and keep a tax-ready record of every job.</p></div>
        <div className="pp-card"><span className="pp-name">⭐ Reviews that compound</span><p style={{ fontSize: 13.5, color: '#6b6477', marginTop: 8 }}>Every completed job builds your rating and pushes you up the directory — your reputation becomes your marketing.</p></div>
      </div>
      <div className="pp-grid" style={{ gridTemplateColumns: 'minmax(280px, 460px)', justifyContent: 'center' }}>
        <div className="pp-card hot">
          <span className="pp-flag">Flat rate</span>
          <span className="pp-name">{SERVICE_PROVIDER_PRICING.name}</span>
          <div className="pp-price">{fmtPrice(SERVICE_PROVIDER_PRICING.price)}<small> /month</small></div>
          <span className="pp-desc">{SERVICE_PROVIDER_PRICING.description}</span>
          <ul className="pp-feats">{SERVICE_PROVIDER_PRICING.features.map((f, i) => <li key={i}>{f}</li>)}</ul>
          <button className="pp-cta" onClick={() => onAuth('service_provider', 'register')}>Create your profile</button>
        </div>
      </div>
    </div>
  </PublicShell>
);

const AuthModal = ({ type, mode, onClose, onLogin, onSwitchMode, onSwitchType }) => {
  const [formData, setFormData] = useState({ email: '', password: '', firstName: '', lastName: '', phone: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (mode === 'register' && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const endpoint = mode === 'login' ? `/api/auth/login/${type}` : `/api/auth/register/${type}`;
      const body = mode === 'login' 
        ? { email: formData.email, password: formData.password }
        : { ...formData };
      // Referral attribution rides along at registration only (v4.14)
      if (mode === 'register') { try { const r = sessionStorage.getItem('propmn-ref'); if (r) body.ref = r; } catch { /* ignore */ } }

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || 'Something went wrong');

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onLogin(data.user);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const typeLabels = {
    landlord: 'Landlord',
    renter: 'Tenant',
    service_provider: 'Service Provider'
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-8 relative max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">{mode === 'login' ? 'Welcome Back' : 'Create Account'}</h2>
          <p className="text-gray-500">{typeLabels[type]} {mode === 'login' ? 'Login' : 'Registration'}</p>
        </div>

        {/* Type Switcher for Login */}
        {mode === 'login' && (
          <div className="flex gap-2 mb-6">
            {Object.entries(typeLabels).map(([key, label]) => (
              <button
                key={key}
                onClick={() => onSwitchType(key)}
                className={`flex-1 py-2 text-sm rounded-lg transition ${type === key ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={e => setFormData({...formData, firstName: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={e => setFormData({...formData, lastName: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={e => setFormData({...formData, email: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={e => setFormData({...formData, phone: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={e => setFormData({...formData, password: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent pr-10"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {mode === 'register' && (
              <p className="text-xs text-gray-400 mt-1">Min 8 characters, include uppercase, lowercase and a number</p>
            )}
          </div>

          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                required
                value={formData.confirmPassword}
                onChange={e => setFormData({...formData, confirmPassword: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50"
          >
            {loading ? 'Please wait…' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <p className="mt-6 text-center text-gray-600">
          {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
          <button onClick={() => onSwitchMode(mode === 'login' ? 'register' : 'login')} className="text-indigo-600 font-medium hover:text-indigo-700">
            {mode === 'login' ? 'Sign Up' : 'Sign In'}
          </button>
        </p>

        {mode === 'login' && (
          <p className="mt-2 text-center">
            <a href="/forgot-password" className="text-sm text-gray-500 hover:text-indigo-600">Forgot password?</a>
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================
// DASHBOARD IMPORTS (Lazy loaded based on role)
// ============================================
// ============================================
// MAIN APP COMPONENT

// ============================================
// TENANT INVITE SIGNUP PAGE
// ============================================
function TenantInviteSignup({ onLogin }) {
  const params = new URLSearchParams(window.location.search);
  const inviteCode = params.get('code');
  const [step, setStep] = useState(inviteCode ? 'register' : 'error');
  const [mode, setMode] = useState('register'); // 'register' | 'login' — login keeps the invite code
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteInfo, setInviteInfo] = useState(null);

  useEffect(() => {
    if (!inviteCode) return;
    // Validate invite code
    fetch(`${API_URL}/api/tenant/validate-invite?code=${inviteCode}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setInviteInfo(data); })
      .catch(console.error);
  }, [inviteCode]);

  // Existing-account path: log in, then accept the invite with the same code.
  const handleLoginAccept = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/login/renter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');

      const inviteRes = await fetch(`${API_URL}/api/tenant/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${data.token}` },
        body: JSON.stringify({ inviteCode })
      });
      if (!inviteRes.ok) {
        const d = await inviteRes.json().catch(() => null);
        throw new Error(d?.error || 'Could not accept the invite with this account.');
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onLogin(data.user);
      window.location.href = '/';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true); setError('');
    try {
      // First register as renter
      const regRes = await fetch(`${API_URL}/api/auth/register/renter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password, firstName: formData.firstName, lastName: formData.lastName, phone: formData.phone })
      });
      const regData = await regRes.json();
      if (!regRes.ok) throw new Error(regData.error || 'Registration failed');

      // Then accept the invite — if this fails the renter MUST be told:
      // their account exists but is NOT linked to the property.
      const inviteRes = await fetch(`${API_URL}/api/tenant/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${regData.token}` },
        body: JSON.stringify({ inviteCode })
      });
      if (!inviteRes.ok) {
        const d = await inviteRes.json().catch(() => null);
        // Account was created but the property link failed (wrong email, expired
        // invite, already accepted). Surface it — do NOT silently drop them into
        // an empty dashboard.
        throw new Error(
          `Your account was created, but we couldn't link it to the property: ` +
          `${d?.error || 'invite could not be accepted'} ` +
          `You can log in with your new account, and ask your landlord to re-send the invite to this email.`
        );
      }

      localStorage.setItem('token', regData.token);
      localStorage.setItem('user', JSON.stringify(regData.user));
      onLogin(regData.user);
      window.location.href = '/';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (step === 'error' || !inviteCode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Invite Link</h2>
          <p className="text-gray-500 mb-6">This invite link is invalid or has expired.</p>
          <a href="/" className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold">Go Home</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <img src="/propmn-logo.png" alt="PropMN" className="h-8" />
          <h2 className="text-xl font-bold text-gray-900 mt-2">Accept Your Invite</h2>
          {inviteInfo && (
            <div className="mt-3 p-3 bg-indigo-50 rounded-lg text-sm text-indigo-700">
              <p>You've been invited to: <strong>{inviteInfo.propertyAddress}</strong></p>
              <p>Rent: <strong>${inviteInfo.rentAmount}/mo</strong></p>
            </div>
          )}
        </div>
        {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
        {mode === 'login' ? (
        <form onSubmit={handleLoginAccept} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Password</label><input type="password" required value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
          <button type="submit" disabled={loading} className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50">
            {loading ? 'Signing In…' : 'Sign In & Accept Invite'}
          </button>
          <p className="mt-4 text-center text-sm text-gray-500">Need an account? <button type="button" onClick={() => { setMode('register'); setError(''); }} className="text-indigo-600 font-medium">Create one</button></p>
        </form>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">First Name</label><input type="text" required value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label><input type="text" required value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label><input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Password</label><input type="password" required value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label><input type="password" required value={formData.confirmPassword} onChange={e => setFormData({...formData, confirmPassword: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
          <button type="submit" disabled={loading} className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50">
            {loading ? 'Creating Account…' : 'Create Account & Accept Invite'}
          </button>
        <p className="mt-4 text-center text-sm text-gray-500">Already have an account? <button type="button" onClick={() => { setMode('login'); setError(''); }} className="text-indigo-600 font-medium">Sign in & accept invite</button></p>
        </form>
        )}
      </div>
    </div>
  );
}

// ============================================
// ─── Admin Login Page ─────────────────────────────────────────────────────────
function AdminLoginPage({ onLogin }) {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [mfaToken, setMfaToken] = React.useState('');
  const [mfaAsked, setMfaAsked] = React.useState(false); // server said mfaRequired

  // Warm the admin console chunk while credentials are being typed
  React.useEffect(() => { prefetchDashboard('admin'); }, []);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/login/admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mfaToken ? { email, password, mfaToken } : { email, password })
      });
      const data = await res.json();
      if (!res.ok) {
        // MFA gate (v4.78): the password was right but a TOTP code is
        // needed — reveal the code field instead of a dead-end error.
        if (data.mfaRequired) { setMfaAsked(true); setError(mfaToken ? data.error : ''); setLoading(false); return; }
        throw new Error(data.error || 'Login failed');
      }
      if (data.user?.role !== 'admin') throw new Error('Not an admin account');
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white">PropMN</h1>
          <p className="text-gray-400 mt-2">Admin Console</p>
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Admin Sign In</h2>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="admin@propmn.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="••••••••"
                required
              />
            </div>
            {mfaAsked && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Authenticator code</label>
                <input
                  type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} autoFocus
                  value={mfaToken}
                  onChange={e => setMfaToken(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent tracking-widest"
                  placeholder="6-digit code"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Two-factor is on for this account — enter the code from your authenticator app.</p>
              </div>
            )}
            <button
              type="submit"
              disabled={loading || (mfaAsked && mfaToken.length !== 6)}
              className="w-full py-3 bg-gray-900 text-white rounded-xl font-semibold hover:bg-gray-800 disabled:opacity-50 transition"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
          <p className="text-center text-xs text-gray-400 mt-6">
            Restricted access — authorised personnel only
          </p>
        </div>
      </div>
    </div>
  );
}

function AppRoutes() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Normalize trailing slashes so '/tenant/signup/' (email clients add these) still routes correctly
  const path = window.location.pathname.replace(/\/+$/, '') || '/';

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (storedUser && token) {
        const parsed = JSON.parse(storedUser);
        // Validate it's a real user object before setting
        if (parsed && parsed.role && (parsed.id || parsed._id)) {
          setUser(parsed);
          // Returning user: start their dashboard chunk downloading immediately,
          // in parallel with this first render pass.
          prefetchDashboard(parsed.role);
        } else {
          // Malformed user object — clear and force re-login
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
    } catch (e) {
      // Corrupted localStorage (extension interference, storage quota, partial write)
      // Clear bad data and show login instead of blank screen
      console.error('Failed to restore session:', e);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  }, []);

  // Global auth-failure interceptor: when the JWT expires mid-session or the
  // account is suspended, force a clean logout instead of silently failing fetches.
  useEffect(() => {
    const origFetch = window.fetch;
    window.fetch = async (...args) => {
      const res = await origFetch(...args);
      try {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
        if (url.includes('/api/') && (res.status === 401 || res.status === 403)) {
          const clone = res.clone();
          const data = await clone.json().catch(() => null);
          if (data && (data.code === 'TOKEN_EXPIRED' || data.code === 'TOKEN_INVALID' || data.code === 'ACCOUNT_SUSPENDED')) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            if (data.code === 'ACCOUNT_SUSPENDED') {
              alert('Your account has been suspended. Please contact support@propmn.com.');
            } else {
              alert('Your session has expired. Please log in again.');
            }
            window.location.href = '/';
          }
        }
      } catch (e) { /* never let the interceptor break a request */ }
      return res;
    };
    return () => { window.fetch = origFetch; };
  }, []);

  const handleLogin = (userData) => setUser(userData);
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Public routes (no login required)
  if (path === '/forgot-password') return <ForgotPasswordPage />;
  if (path === '/reset-password') return <ResetPasswordPage />;
  if (path === '/verify-email' || path.startsWith('/verify-email?')) return <EmailVerificationPage />;
  if (path === '/tenant/signup') return <TenantInviteSignup onLogin={handleLogin} />;
  if (path === '/staff/signup') return <StaffInviteSignup onLogin={handleLogin} />;
  if (path === '/owner/signup') return <OwnerInviteSignup onLogin={handleLogin} />;
  if (path === '/team/signup') return <TeamInviteSignup onLogin={handleLogin} />;
  if (path === '/enterprise') return <ErrorBoundary><EnterprisePage /></ErrorBoundary>;
  if (path === '/how-it-works') return <ErrorBoundary><HowItWorksPage /></ErrorBoundary>;
  if (path === '/for-agencies') return <ErrorBoundary><ForAgenciesPage /></ErrorBoundary>;
  if (path === '/verify') return <ErrorBoundary><VerifyPage /></ErrorBoundary>;
  if (path.startsWith('/history/')) return <ErrorBoundary><RentHistoryPage code={path.slice('/history/'.length)} /></ErrorBoundary>;
  if (path.startsWith('/sign/')) return <ErrorBoundary><SignPage /></ErrorBoundary>;
  if (path.startsWith('/p/')) return <ErrorBoundary><PartnerPage slug={path.slice(3)} /></ErrorBoundary>;
  if (path === '/gov' || path.startsWith('/gov?')) return <ErrorBoundary><GovPortalPage /></ErrorBoundary>;
  if (path === '/baltimore') return <ErrorBoundary><BaltimorePage /></ErrorBoundary>;
  if (path === '/philadelphia') return <ErrorBoundary><PhiladelphiaPage /></ErrorBoundary>;
  if (path === '/wilmington') return <ErrorBoundary><WilmingtonPage /></ErrorBoundary>;
  if (path === '/security') return <ErrorBoundary><SecurityPage /></ErrorBoundary>;
  if (path === '/guides') return <ErrorBoundary><GuidesPage /></ErrorBoundary>;
  if (path === '/commercial') return <ErrorBoundary><CommercialPage /></ErrorBoundary>;
  if (path === '/agency/signup') return <AgencySignup onLogin={handleLogin} />;
  // Public listing page — /listing/CODE, the browsing door to the apply flow
  if (path.startsWith('/listing/')) {
    const code = path.slice('/listing/'.length).toUpperCase();
    return <ErrorBoundary><ListingPage code={code} /></ErrorBoundary>;
  }
  // Public rental application — /apply/CODE, no account required
  if (path.startsWith('/apply/')) {
    const code = path.slice('/apply/'.length).toUpperCase();
    return <ErrorBoundary><ApplyPage code={code} /></ErrorBoundary>;
  }
  const publicPage = { '/terms': TermsOfService, '/privacy': PrivacyPolicy, '/changelog': ChangelogPage, '/extended-stay': ExtendedStayPage, '/housing-vouchers': HousingVouchersPage, '/delete-account': DeleteAccountPage }[path];
  if (publicPage) return <ErrorBoundary><PublicDocPage Body={publicPage} onLogin={handleLogin} /></ErrorBoundary>;

  // Admin route — handle /admin and /admin/
  // Admin console path — deliberately NOT /admin (scanner noise reduction).
  // Change the constant to relocate; the Amplify catch-all rewrite covers any path.
  // Real protection is the admin JWT on every /api/admin route, not this name.
  const ADMIN_PATH = '/mn-console';
  if (path === ADMIN_PATH) {
    if (user?.role === 'admin') {
      return <ErrorBoundary><AdminDashboard /></ErrorBoundary>;
    }
    return <AdminLoginPage onLogin={handleLogin} />;
  }

  // Not logged in - show landing page
  if (!user) {
    return <LandingPage onLogin={handleLogin} />;
  }

  // Logged in - show appropriate dashboard.
  // Admins land on the console from ANY path — before this, an admin at '/'
  // matched no role branch and got a blank page (customer-walk find).
  if (user?.role === 'admin') {
    return <ErrorBoundary><AdminDashboard /></ErrorBoundary>;
  }
  return (
    <ErrorBoundary>
      {user.role === 'landlord' && <LandlordDashboard user={user} onLogout={handleLogout} />}
      {user.role === 'renter' && <RenterDashboard user={user} onLogout={handleLogout} />}
      {user.role === 'service_provider' && <ServiceProviderDashboard user={user} onLogout={handleLogout} />}
      {user.role === 'agency' && <AgencyDashboard user={user} onLogout={handleLogout} />}
      {user.role === 'owner' && <OwnerDashboard user={user} onLogout={handleLogout} />}
      {/* Staff (v4.48): the backend re-scopes every request to the
          employer's account and gates it by role — the UI is the landlord
          workspace with a banner saying whose account this is. */}
      {user.role === 'staff' && (
        <>
          <div className="bg-indigo-900 text-white text-center text-xs py-1.5 px-3">
            Team workspace — you're working in your employer's account{user.staffRole ? ` as ${user.staffRole}` : ''}. Actions outside your role are blocked and your changes are recorded under your name.
          </div>
          <LandlordDashboard user={user} onLogout={handleLogout} />
        </>
      )}
    </ErrorBoundary>
  );
}

// Single Suspense boundary over all routes: any lazy chunk that's still
// downloading shows the same spinner the app already uses for session
// restore, so slow-network users see one consistent loading state.
export default function App() {
  // Referral capture (v4.14): ?ref=slug from partner links survives the
  // whole visit in sessionStorage and rides along at registration.
  React.useEffect(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get('ref');
      if (ref && /^[a-z0-9-]{2,40}$/i.test(ref)) sessionStorage.setItem('propmn-ref', ref.toLowerCase());
    } catch { /* ignore */ }
  }, []);
  return (
    <Suspense fallback={<RouteLoading />}>
      <AppRoutes />
    </Suspense>
  );
}
